/**
 * GTM client-side helpers — route tracking and web vitals.
 *
 * GTM itself is loaded via a standard inline <script> in src/html.js.
 * Consent defaults are also set inline in html.js (before GTM).
 *
 * This module handles client-side SPA concerns only:
 *   - sendRouteChange(): pushes gatsby-route-change on every client navigation
 *   - initWebVitals(): registers Core Web Vitals handlers that push to dataLayer
 *
 * Execution order on page load:
 *   1. html.js inline script: consent defaults (all denied, wait_for_update: 500)
 *   2. html.js inline script: GTM bootstrap + async gtm.js load
 *   3. consent.ts: applyConsentDefaults() — updates consent if stored prefs exist
 *   4. initWebVitals() (onClientEntry) — registers web-vitals handlers
 *   5. sendRouteChange() (onRouteUpdate) — fired on every client-side navigation
 */

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/** Injected metrics names tracked to prevent duplicates (same logic as plugin) */
const reportedMetrics = new Set<string>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Track whether the first route update (initial page hydration) has been skipped.
 * The initial page load is already captured by GTM's built-in page_view trigger,
 * so we only push gatsby-route-change on subsequent SPA navigations.
 */
let isInitialRouteUpdate = true;

/**
 * Pushes a route-change event to dataLayer after a 50ms delay.
 * Skips the initial page load to prevent the event from firing twice.
 */
export const sendRouteChange = (): void => {
  if (!IS_PRODUCTION) return;

  if (isInitialRouteUpdate) {
    isInitialRouteUpdate = false;
    return;
  }

  setTimeout(() => {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'gatsby-route-change' });
    }
  }, 50);
};

/**
 * Registers web-vitals handlers that push Core Web Vitals to dataLayer.
 *
 * web-vitals v4 fires each handler once with the finalised value (default
 * reportAllChanges: false) — CLS/LCP resolve on page-hidden/pagehide, INP on
 * page-hidden. We therefore report synchronously inside the callback: deferring
 * with a setTimeout risks the timer never running before the tab is frozen or
 * unloaded, dropping the final CLS/LCP push. Metrics are deduplicated per name
 * using a Set as a belt-and-braces guard against any repeat callback.
 */
export const initWebVitals = (): void => {
  if (!IS_PRODUCTION) return;

  // Dynamic import — only loads web-vitals bundle when needed (same as plugin)
  import('web-vitals')
    .then(({ onCLS, onINP, onLCP }) => {
      const report = (metric: { name: string; id: string; value: number }) => {
        if (reportedMetrics.has(metric.name)) return;
        reportedMetrics.add(metric.name);

        if (!Array.isArray(window.dataLayer)) return;

        window.dataLayer.push({
          event: 'core-web-vitals',
          webVitalsMeasurement: {
            name: metric.name,
            id: metric.id,
            // CLS is unitless 0-1 range; multiply by 1000 to normalise like the plugin does
            value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
          },
        });
      };

      onCLS(report);
      onLCP(report);
      // INP — responsiveness metric that replaced FID in Core Web Vitals
      onINP(report);
    })
    .catch(() => {
      // web-vitals bundle failed to load — non-critical, skip silently
    });
};
